/**
 * 조직/브랜드별 매장 목록 조회 훅
 * storeApi.getStores()를 사용하여 동일 매장 그룹 또는 조직/브랜드 매장 목록을 조회
 *
 * #249: brandId 지원 추가 - 조직 없이 브랜드만 있는 매장에서도
 *       동일 브랜드 소속 매장 목록을 조회할 수 있도록 개선
 *
 * #266: 위치 필터 미적용 — 동일 조직/브랜드 매장 전체 노출이 목적.
 *       BE radius 기본 10km/상한 100km 제약으로 위치 기반 조회는 원거리 매장을
 *       누락시켜 "자기 매장만 보이는" 증상을 유발. 근처 매장 디스커버리는
 *       별도 훅(useAllStores)이 담당.
 */
import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { storeApi, organizationApi, getErrorMessage, transformApiStoreToLocal } from '@/lib/api';
import type { OrganizationSummary } from '@/lib/api/types';
import type { Store as LocalStore } from '@/data';
import { logger } from '@/utils/logger';

interface UseOrganizationStoresState {
  organization: OrganizationSummary | null;
  stores: LocalStore[];
  loading: boolean;
  error: Error | null;
}

interface UseOrganizationStoresReturn extends UseOrganizationStoresState {
  refetch: () => Promise<void>;
  isApiData: boolean;
}

/**
 * 조직/브랜드별 매장 목록 조회 훅
 * @param organizationId 조직 ID 또는 슬러그
 * @param brandId 브랜드 ID (조직 없이 브랜드만 있는 매장용)
 * @param sourceStoreId 지정 시 해당 매장의 활성 매장 그룹 기준으로 조회
 */
export function useOrganizationStores(
  organizationId: string | number | null | undefined,
  brandId?: number | null,
  sourceStoreId?: number | null
): UseOrganizationStoresReturn {
  const owner = useMemo(() => ({ organizationId, brandId, sourceStoreId }),
    [organizationId, brandId, sourceStoreId]);
  const activeOwner = useRef<typeof owner | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<UseOrganizationStoresState>({
    organization: null,
    stores: [],
    loading: false,
    error: null,
  });
  const [isApiData, setIsApiData] = useState(false);

  useLayoutEffect(() => {
    activeOwner.current = owner;
    setState({ organization: null, stores: [], loading: false, error: null });
    setIsApiData(false);
    return () => {
      activeOwner.current = null;
      controllerRef.current?.abort();
    };
  }, [owner]);

  const fetchStores = useCallback(async () => {
    if (activeOwner.current !== owner) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const isCurrent = () => activeOwner.current === owner &&
      controllerRef.current === controller && !controller.signal.aborted;
    const { organizationId: orgId, brandId: brdId, sourceStoreId: srcStoreId } = owner;
    if (!orgId && !brdId && !srcStoreId) {
      setState({
        organization: null,
        stores: [],
        loading: false,
        error: null,
      });
      setIsApiData(false);
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      let numericOrgId: number | undefined;
      let orgSummary: OrganizationSummary | null = null;

      // 조직 ID가 있으면 조직 정보 조회
      if (orgId) {
        const orgResponse = await organizationApi.getOrganization(String(orgId), controller.signal);
        if (!isCurrent()) return;
        numericOrgId = orgResponse.data.id;
        orgSummary = {
          id: orgResponse.data.id,
          slug: orgResponse.data.slug,
          nickname: orgResponse.data.nickname ?? undefined,
          name: orgResponse.data.name,
        };
      }

      // 통합 API로 매장 목록 조회 (매장 그룹, 조직 또는 브랜드 기반)
      // #266: 위치 필터(lat/lng/radius) 일체 전송하지 않음.
      //       동일 조직/브랜드 매장 전체를 이름순으로 반환받아
      //       거리와 무관하게 모든 동일 브랜드 매장이 팝업에 노출되도록 보장.
      //       건수 상한은 per_page=100.
      const storesResponse = await storeApi.getStores({
        organization_id: numericOrgId,
        brand_id: srcStoreId ? undefined : brdId ?? undefined,
        source_store_id: srcStoreId ?? undefined,
        per_page: 100,
        sort: 'name',
      }, controller.signal);
      if (!isCurrent()) return;

      const localStores = storesResponse.data.map(transformApiStoreToLocal);

      setState({
        organization: orgSummary,
        stores: localStores,
        loading: false,
        error: null,
      });
      setIsApiData(true);
    } catch (err) {
      if (!isCurrent()) return;
      logger.error('[useOrganizationStores] API 호출 실패:', err);
      const message = getErrorMessage(err, '매장 목록을 불러오는데 실패했습니다');
      setState({
        organization: null,
        stores: [],
        loading: false,
        error: new Error(message),
      });
      setIsApiData(false);
    }
  }, [owner]);

  useEffect(() => {
    void fetchStores();
  }, [fetchStores]);

  const refetch = fetchStores;

  return {
    ...state,
    refetch,
    isApiData,
  };
}
